import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { adminService, type AdminApprovalPayload, type AdminPlanPayload, type FormConfigPayload } from '../services/admin.service'
import type { ReviewApprovalPayload } from '../types/admin.types'

export const adminQueryKeys = {
  stats: ['admin', 'stats'] as const,
  requestsCount: ['admin', 'requests-count'] as const,
  pendingApprovals: ['admin', 'pending-approvals'] as const,
  users: ['admin', 'users'] as const,
  subscriptions: ['admin', 'subscriptions'] as const,
  formConfig: (formType: string) => ['admin', 'form-config', formType] as const,
  settings: ['admin', 'settings'] as const,
  auditLogs: ['admin', 'audit-logs'] as const,
}

export function useAdminStats() {
  return useQuery({
    queryKey: adminQueryKeys.stats,
    queryFn: adminService.getStats,
    staleTime: 30000,
  })
}

export function useAdminRequestsCount() {
  return useQuery({
    queryKey: adminQueryKeys.requestsCount,
    queryFn: adminService.getPendingRequestsCount,
    staleTime: 30000,
  })
}

export function useAdminPendingApprovals() {
  return useQuery({
    queryKey: adminQueryKeys.pendingApprovals,
    queryFn: adminService.getPendingApprovals,
  })
}

export function useAdminUsers() {
  return useQuery({
    queryKey: adminQueryKeys.users,
    queryFn: adminService.getUsersOverview,
  })
}

export function useAdminUserActivity(userId?: number) {
  return useQuery({
    queryKey: ['admin', 'user-activity', userId],
    queryFn: () => (userId ? adminService.getUserActivity(userId) : null),
    enabled: Boolean(userId),
  })
}

export function useAdminSubscriptions() {
  return useQuery({
    queryKey: adminQueryKeys.subscriptions,
    queryFn: adminService.getSubscriptionsOverview,
  })
}

export function useFormConfigQuery(formType: 'SELLER' | 'DEALER') {
  return useQuery({
    queryKey: adminQueryKeys.formConfig(formType),
    queryFn: () => adminService.getFormConfig(formType),
  })
}

export function useAdminSettingsQuery() {
  return useQuery({
    queryKey: adminQueryKeys.settings,
    queryFn: adminService.getSettings,
  })
}

export function useAdminAuditLogsQuery() {
  return useQuery({
    queryKey: adminQueryKeys.auditLogs,
    queryFn: adminService.getAuditLogs,
  })
}

export function useApproveRequestMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload?: AdminApprovalPayload }) =>
      adminService.approveRequest(id, payload),
    onSuccess: () => {
      toast.success('Approval request approved successfully.')
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.pendingApprovals })
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.stats })
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.users })
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.requestsCount })
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.auditLogs })
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to approve request.')
    },
  })
}

export function useRejectRequestMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload?: ReviewApprovalPayload }) =>
      adminService.rejectRequest(id, payload),
    onSuccess: () => {
      toast.success('Approval request rejected.')
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.pendingApprovals })
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.stats })
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.users })
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.requestsCount })
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.auditLogs })
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to reject request.')
    },
  })
}

export function useCreatePlanMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: AdminPlanPayload) => adminService.createPlan(payload),
    onSuccess: () => {
      toast.success('Subscription plan created.')
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.subscriptions })
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.auditLogs })
    },
    onError: (error: Error) => toast.error(error.message || 'Failed to create plan.'),
  })
}

export function useUpdatePlanMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: AdminPlanPayload }) =>
      adminService.updatePlan(id, payload),
    onSuccess: () => {
      toast.success('Subscription plan updated.')
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.subscriptions })
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.auditLogs })
    },
    onError: (error: Error) => toast.error(error.message || 'Failed to update plan.'),
  })
}

export function useDuplicatePlanMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => adminService.duplicatePlan(id),
    onSuccess: () => {
      toast.success('Subscription plan duplicated.')
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.subscriptions })
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.auditLogs })
    },
    onError: (error: Error) => toast.error(error.message || 'Failed to duplicate plan.'),
  })
}

export function useArchivePlanMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => adminService.archivePlan(id),
    onSuccess: () => {
      toast.success('Plan status updated.')
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.subscriptions })
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.auditLogs })
    },
    onError: (error: Error) => toast.error(error.message || 'Failed to update plan status.'),
  })
}

export function useDeletePlanMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => adminService.deletePlan(id),
    onSuccess: () => {
      toast.success('Subscription plan deleted.')
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.subscriptions })
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.auditLogs })
    },
    onError: (error: Error) => toast.error(error.message || 'Failed to delete plan.'),
  })
}

export function useUpdateFormConfigMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ formType, payload }: { formType: 'SELLER' | 'DEALER'; payload: FormConfigPayload }) =>
      adminService.updateFormConfig(formType, payload),
    onSuccess: (_, variables) => {
      toast.success(`${variables.formType} Form Configuration saved!`)
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.formConfig(variables.formType) })
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.auditLogs })
    },
    onError: (error: Error) => toast.error(error.message || 'Failed to save form config.'),
  })
}

export function useUpdateSettingsMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (settings: { key: string; value: any }[]) => adminService.updateSettings(settings),
    onSuccess: () => {
      toast.success('Platform settings updated.')
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.settings })
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.auditLogs })
    },
    onError: (error: Error) => toast.error(error.message || 'Failed to save settings.'),
  })
}
